import { useMediaQuery } from "@mui/material";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useReducer,
  useState,
} from "react";
import {
  GUIDE_PAGES,
  GuidePageId,
  guideWidths,
  shownEntries,
} from "./guidePages.ts";
import {
  GuideAction,
  guideClosed,
  guideReducer,
  GuideState,
  pickedEntry,
} from "./guideState.ts";

export type Guide = {
  // the open page, where it has a guide
  page?: GuidePageId;
  // whether the guide is open on that page
  shown: boolean;
  // the entry that is open on that page
  picked?: string;
  // the entry being pointed at
  pointed?: string;
  // where the open guide goes: beside the page with the navigation folded to
  // its rail, or over the page
  foldsNavigation: boolean;
  overPage: boolean;
  // the entries of the page's elements that are there, in their order
  entries: readonly string[];
  // a marker tells it is on the page; what it returns tells it is gone
  register: (entry: string) => () => void;
  dispatch: (action: GuideAction) => void;
};

const GuideContext = createContext<Guide | undefined>(undefined);

/** The guide of the case pages under it; undefined anywhere else. */
export const useGuide = (): Guide | undefined => useContext(GuideContext);

// the widths of a page without a guide: nothing folds for it
const NO_PAGE = { minWidth: 0, entries: [], general: [] };

export const GuideProvider = ({
  page,
  initial = guideClosed,
  children,
}: {
  page: GuidePageId | undefined;
  initial?: GuideState;
  children: ReactNode;
}) => {
  const [state, dispatch] = useReducer(guideReducer, initial);
  const { beside, besideRail } = guideWidths(
    page === undefined ? NO_PAGE : GUIDE_PAGES[page],
  );
  const fitsBeside = useMediaQuery(`(min-width:${beside}px)`);
  const fitsBesideRail = useMediaQuery(`(min-width:${besideRail}px)`);
  const shown = state.open && page !== undefined;
  // how many markers of each entry are on the page
  const [present, setPresent] = useState<ReadonlyMap<string, number>>(
    new Map(),
  );
  const register = useCallback((entry: string) => {
    const by = (step: number) =>
      setPresent((known) =>
        new Map(known).set(entry, (known.get(entry) ?? 0) + step),
      );
    by(1);
    return () => by(-1);
  }, []);
  const entries = useMemo(
    () =>
      page === undefined
        ? []
        : shownEntries(
            GUIDE_PAGES[page],
            new Set(
              [...present].filter(([, count]) => count > 0).map(([e]) => e),
            ),
          ),
    [page, present],
  );

  const guide = useMemo(
    () => ({
      page,
      shown,
      picked: page === undefined ? undefined : pickedEntry(state, page),
      pointed: state.pointed,
      foldsNavigation: shown && !fitsBeside && fitsBesideRail,
      overPage: shown && !fitsBesideRail,
      entries,
      register,
      dispatch,
    }),
    [page, shown, state, fitsBeside, fitsBesideRail, entries, register],
  );

  return (
    <GuideContext.Provider value={guide}>{children}</GuideContext.Provider>
  );
};

const MarkersContext = createContext(true);

/** Whether a marker under this scope is drawn. */
export const useMarkersShown = () => useContext(MarkersContext);

/**
 * Keeps the markers of what it holds from being drawn: a page marks each kind
 * of element once, on the first card that shows it.
 */
export const GuideMarkers = ({
  shown,
  children,
}: {
  shown: boolean;
  children: ReactNode;
}) => (
  <MarkersContext.Provider value={shown}>{children}</MarkersContext.Provider>
);
